import { useState, useRef, useCallback, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import { useSnackbar } from "notistack";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import {
  updateCustomerBackground,
  deleteCustomerBackground,
  resetBackgroundErrors,
  resetBackgroundSuccess,
} from "../../../../redux/slices/customersSlice";

const CoverImageUpload = ({
  customerId,
  currentBackground = null,
  height = 250,
}: any) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // Redux state
  const {
    backgroundUploading,
    backgroundDeleting,
    backgroundError,
    backgroundDeleteError,
    backgroundSuccess,
    backgroundDeleteSuccess,
  } = useAppSelector((state) => state.customers);

  // Local state
  const [previewImage, setPreviewImage] = useState(currentBackground);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<any>(null);

  // Handle success/error notifications
  useEffect(() => {
    if (backgroundSuccess) {
      enqueueSnackbar("Background image uploaded successfully!", {
        variant: "success",
      });
      dispatch(resetBackgroundSuccess());
    }
  }, [backgroundSuccess, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (backgroundDeleteSuccess) {
      enqueueSnackbar("Background image deleted successfully!", {
        variant: "success",
      });
      setPreviewImage(null);
      dispatch(resetBackgroundSuccess());
    }
  }, [backgroundDeleteSuccess, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (backgroundError) {
      enqueueSnackbar(`Upload failed: ${backgroundError}`, {
        variant: "error",
      });
      dispatch(resetBackgroundErrors());
    }
  }, [backgroundError, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (backgroundDeleteError) {
      enqueueSnackbar(`Delete failed: ${backgroundDeleteError}`, {
        variant: "error",
      });
      dispatch(resetBackgroundErrors());
    }
  }, [backgroundDeleteError, enqueueSnackbar, dispatch]);

  // Update preview when prop changes
  useEffect(() => {
    setPreviewImage(currentBackground);
  }, [currentBackground]);

  // File validation
  const validateFile = useCallback((file: any) => {
    if (!file) return { isValid: false, error: "No file selected" };

    if (!file.type.startsWith("image/")) {
      return { isValid: false, error: "Please select an image file" };
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return { isValid: false, error: "Image must be smaller than 10MB" };
    }

    return { isValid: true };
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback(
    (file: any) => {
      const validation = validateFile(file);

      if (!validation.isValid) {
        enqueueSnackbar(validation.error, { variant: "error" });
        return;
      }

      if (!customerId) {
        enqueueSnackbar("Customer ID is required", { variant: "error" });
        return;
      }

      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setPreviewImage(previewUrl);

      // Upload to server
      dispatch(updateCustomerBackground({ imageFile: file, customerId }));
    },
    [customerId, dispatch, enqueueSnackbar, validateFile]
  );

  // Handle input change
  const handleInputChange = useCallback(
    (event: any) => {
      const file = event.target.files[0];
      if (file) {
        handleFileSelect(file);
      }
      // Clear input
      event.target.value = "";
    },
    [handleFileSelect]
  );

  // Handle drag events
  const handleDragOver = useCallback((event: any) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: any) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: any) => {
      event.preventDefault();
      setDragOver(false);

      const file = event.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  // Handle click to upload
  const handleClick = useCallback(() => {
    if (!backgroundUploading && !backgroundDeleting) {
      fileInputRef.current?.click();
    }
  }, [backgroundUploading, backgroundDeleting]);

  // Handle delete
  const handleDelete = useCallback(
    (event: any) => {
      event.stopPropagation();

      if (!customerId) {
        enqueueSnackbar("Customer ID is required", { variant: "error" });
        return;
      }

      dispatch(deleteCustomerBackground(customerId));
    },
    [customerId, dispatch, enqueueSnackbar]
  );

  const isLoading = backgroundUploading || backgroundDeleting;

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          width: "100%",
          height: `${height}px`,
          border: dragOver ? "2px solid #555759" : "2px dashed #aaa",
          borderRadius: 0,
          overflow: "hidden",
          backgroundColor: dragOver ? "rgba(25, 118, 210, 0.04)" : "#f7f7f7",
          backgroundImage: previewImage ? `url(${previewImage})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isLoading ? "default" : "pointer",
          position: "relative",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: isLoading ? "#aaa" : "#555759",
            backgroundColor: previewImage
              ? "transparent"
              : isLoading
                ? "#f7f7f7"
                : "rgba(25, 118, 210, 0.04)",
          },
        }}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Loading Overlay */}
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
            }}
          >
            <CircularProgress sx={{ color: "white" }} />
          </Box>
        )}

        {/* Upload Instruction */}
        {!previewImage && !isLoading && (
          <Box sx={{ textAlign: "center", p: 3 }}>
            <UploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Drop background image here
            </Typography>
            <Typography variant="body2" color="textSecondary">
              or click to browse files
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 1, display: "block" }}
            >
              Supports JPG, PNG, GIF up to 10MB
            </Typography>
          </Box>
        )}

        {/* Delete Button */}
        {previewImage && !isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 1,
            }}
          >
            <Tooltip title="Delete background image">
              <IconButton
                onClick={handleDelete}
                sx={{
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                  },
                }}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Hidden File Input */}
        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleInputChange}
        />
      </Box>
    </Box>
  );
};

export default CoverImageUpload;
